import { createFileRoute, stripSearchParams } from '@tanstack/react-router'

import { guardScreen } from '@/app/route-guards'
import { ReviewScreen } from '@/features/review'
import { optional, positiveInt, withDefault, z } from '@/lib/utils/search-params'

const ISSUE_FILTERS = [
  'all',
  'overflow',
  'untranslated',
  'glossary_missing',
  'image_not_approved',
  'image_outdated',
  'comment',
  'stale_edit',
  'font_fallback',
  'not_placed',
  'page_unreadable',
  'rebuild_failed',
] as const

/** Review deep links (§4.1): page, element, side, view, panel tab, issue filter, open image. */
const reviewSearch = z.object({
  page: optional(positiveInt),
  el: optional(positiveInt),
  side: withDefault(z.enum(['src', 'tr']), 'tr'),
  view: withDefault(z.enum(['src', 'both', 'tr', 'swipe']), 'both'),
  tab: withDefault(z.enum(['selection', 'issues', 'images', 'comments']), 'selection'),
  issue: optional(z.enum(ISSUE_FILTERS)),
  img: optional(positiveInt),
})

export type ReviewSearch = z.infer<typeof reviewSearch>

export const Route = createFileRoute('/_auth/documents/$docId/review')({
  validateSearch: reviewSearch,
  search: { middlewares: [stripSearchParams({ side: 'tr', view: 'both', tab: 'selection' })] },
  beforeLoad: ({ context, params }) => guardScreen(context.queryClient, params.docId, 'review'),
  // Runs on link intent too (defaultPreload: 'intent'): pages, state and the first page's rasters.
  loader: ({ context, params, location }) => {
    const search = location.search as Partial<ReviewSearch>
    const view = search.view ?? 'both'
    // The prefetch code loads on first use, so it stays out of the entry bundle.
    void import('@/components/app/canvas/data/prefetch').then((m) =>
      m.prefetchDocumentView(context.queryClient, params.docId, {
        pageNumber: search.page,
        sides: view === 'src' ? ['original'] : view === 'tr' ? ['translated'] : ['original', 'translated'],
      }),
    )
  },
  staleTime: Infinity,
  component: ReviewRoute,
})

function ReviewRoute() {
  const { docId } = Route.useParams()
  return <ReviewScreen docId={docId} />
}
